<script setup lang="ts">

import {storeToRefs} from "pinia"
import {useInterfaceStore} from "~/stores/interface"
import {useSessionsStore} from "~/stores/sessions"
import Roles, {getDefaultPageForRole} from "~/utils/roles"
import {usePermissionsStore} from "~/stores/permissions"
import PushSwitcher from "~/components/notifications/PushSwitcher.vue"
import {useNotificationsStore} from "~/stores/notifications"
import {isSupported as isFirebaseSupported} from "firebase/messaging"
import {useApp} from "~/composables/useApp"
import {useCentrifugoStore} from "~/stores/centrifugo"
import ShowPreferredChannelsOnlySwitcher from "~/components/notifications/ShowPreferredChannelsOnlySwitcher.vue";

const {t} = useI18n()

const {permissions} = storeToRefs(usePermissionsStore())
const sessionsStore = useSessionsStore()
const notificationsStore = useNotificationsStore()
const {lastMessage} = storeToRefs(useCentrifugoStore())

if (!sessionsStore.sessions.length) {
  await sessionsStore.load()
}

const usersAffiliationTitle = computed(() => {
  const {user} = useAuth()

  if ([Roles.ISTAR_DIVISION.value, Roles.TEAM.value, Roles.VIEWER.value].includes(user.value?.role)) {
    return user.value?.divisionName
  }
  if (user.value?.role === Roles.ISTAR_ZONE.value) {
    return user.value?.zoneName
  }
  if (user.value?.role === Roles.OTG_COORDINATOR.value) {
    return user.value?.otgName
  }

  return null
})

await notificationsStore.load()
const unreadNotificationsCount = computed(() => {
  return notificationsStore.unread
})

const routesData = computed(() => {

  const {user} = useAuth()

  const pilotAssignedToTeam = user.value?.role == Roles.TEAM.value && sessionsStore.sessions.length > 0
  const divisionBlocked = user.value?.divisionBlocked === true

  return [
    {
      title: t('appHeader.navigation.flightMatrix'),
      path: '/fly',
      icon: 'mdi-airplane',
      available: permissions.value.includes('fly_page:view'),
    },
    {
      title: t('appHeader.navigation.teamSettings'),
      path: '/team-settings',
      icon: 'mdi-tools',
      available: user.value?.role === Roles.TEAM.value && pilotAssignedToTeam && !divisionBlocked,
    },
    {
      title: t('appHeader.navigation.history'),
      path: '/history',
      available: permissions.value.includes('flight_history:view') && (user.value?.role !== Roles.TEAM.value || pilotAssignedToTeam) && !divisionBlocked,
      icon: 'mdi-history',
    },
    {
      title: t('appHeader.navigation.positionStock'),
      path: '/team-stock',
      icon: 'mdi-package-variant',
      available: (user.value?.role === Roles.TEAM.value && pilotAssignedToTeam && sessionsStore.sessions[0]?.position?.id !== undefined && !divisionBlocked) || 
                 (user.value?.role === Roles.VIEWER.value && !divisionBlocked),
    },
    {
      title: user.value?.role === Roles.OTG_COORDINATOR.value
          ? t('appHeader.navigation.divisionsManagement') : t('appHeader.navigation.divisionManagement'),
      icon: 'mdi-check-decagram-outline',
      children: [
        {
          title: t('appHeader.navigation.combatDuties'),
          path: "/division-admin/sessions",
          available: permissions.value.includes("combat_sessions_page:view") && !divisionBlocked,
          icon: 'mdi-timer-star-outline',
        },
        {
          title: t('appHeader.navigation.zones'),
          path: "/division-admin/zone-requests",
          available: permissions.value.includes("zone-requests:management") && !divisionBlocked,
          icon: 'mdi-map-marker-radius',
        },
        {
          title: t('appHeader.navigation.targets'),
          path: "/division-admin/targets",
          available: permissions.value.includes("targets:management") && !divisionBlocked,
          icon: 'mdi-target-account',
        },
        {
          title: t('appHeader.navigation.divisionSignal'),
          path: "/division-admin/signal",
          available: permissions.value.includes("division:signal-management") && !divisionBlocked,
          icon: '$signal',
        },
        {
          title: t('appHeader.navigation.inventory'),
          path: "/division-admin/inventory",
          available: permissions.value.includes("positions:inventory:manage") && !divisionBlocked,
          icon: 'mdi-clipboard-list-outline',
        },
      ]
    },
    {
      title: t('appHeader.navigation.zoneManagement'),
      icon: 'mdi-hammer-wrench',
      children: [
        {
          title: t('appHeader.navigation.divisionsInZone'),
          path: "/zone-admin/active",
          available: permissions.value.includes("zone_admin:view_divisions") && !divisionBlocked,
          icon: 'mdi-sitemap-outline',
        },
        {
          title: t('appHeader.navigation.requests'),
          path: "/zone-admin/inactive",
          available: permissions.value.includes("zone_admin:view_requests") && !divisionBlocked,
          icon: 'mdi-map-clock-outline',
        },
        {
          title: t('appHeader.navigation.messages'),
          path: "/zone-admin/broadcast",
          available: permissions.value.includes("zone_admin:broadcast") && !divisionBlocked,
          icon: 'mdi-bullhorn',
        },
      ]
    },
    {
      title: t('appHeader.navigation.admin'),
      icon: 'mdi-security',
      children: [
        {
          path: '/admin/divisions',
          title: t('appHeader.navigation.divisions'),
          available: permissions.value.includes('admin:divisions_management') && !divisionBlocked,
          icon: 'mdi-sitemap',
        },
        {
          path: '/admin/teams',
          title: t('appHeader.navigation.teams'),
          available: permissions.value.includes('admin:teams_management') && !divisionBlocked,
          icon: 'mdi-account-group',
        },
        {
          path: '/admin/users',
          title: t('appHeader.navigation.users'),
          available: permissions.value.includes('admin:users_management') && !divisionBlocked,
          'icon': 'mdi-account',
        },
        {
          path: '/admin/zones',
          title: t('appHeader.navigation.zones'),
          available: permissions.value.includes('admin:zones_management') && !divisionBlocked,
          icon: 'mdi-map-marker-radius',
        },
        {
          path: '/admin/otg',
          title: t('appHeader.navigation.otg'),
          available: permissions.value.includes('admin:otg_management') && !divisionBlocked,
          icon: 'mdi-map-marker-distance',
        },
        {
          path: '/admin/osg',
          title: t('appHeader.navigation.osg'),
          available: permissions.value.includes('admin:osg_management') && !divisionBlocked,
          icon: 'mdi-map',
        },
        {
          path: '/admin/chat_channels',
          title: t('appHeader.navigation.chats'),
          available: permissions.value.includes('admin:chats_management') && !divisionBlocked,
          icon: 'mdi-message',
        },
        {
          path: '/admin/sessions',
          title: t('appHeader.navigation.sessionsBCh'),
          available: permissions.value.includes('admin:sessions') && !divisionBlocked,
          icon: 'mdi-timer-star-outline',
        },
        {
          path: '/admin/ammo',
          title: t('appHeader.navigation.ammo'),
          available: permissions.value.includes('admin:ammo_management') && !user.value?.useStockItemsInventory && !divisionBlocked,
          icon: 'mdi-ammunition',
        },
        {
          path: '/admin/drones',
          title: t('appHeader.navigation.drones'),
          available: permissions.value.includes('admin:stock_management') && !divisionBlocked,
          icon: 'mdi-quadcopter',
        },
        {
          path: '/admin/ammunition',
          title: t('appHeader.navigation.ammunition'),
          available: permissions.value.includes('admin:stock_management') && !divisionBlocked,
          icon: 'mdi-ammunition',
        },
        {
          path: '/admin/positions',
          title: t('appHeader.navigation.positions'),
          available: permissions.value.includes('positions:interface') && !divisionBlocked,
          icon: 'mdi-map-marker-star',
        },
        {
          path: '/admin/merger',
          title: t('appHeader.navigation.antiduplicate'),
          available: permissions.value.includes('admin:merger') && !divisionBlocked,
          icon: 'mdi-content-duplicate',
        },
        {
          path: '/admin/faqs',
          title: t('appHeader.navigation.faqEditor'),
          available: permissions.value.includes('admin:faq_management') && !divisionBlocked,
          icon: 'mdi-help-box-multiple',
        },
        {
          path: '/admin/news',
          title: t('appHeader.navigation.newsEditor'),
          available: permissions.value.includes('admin:news_management') && !divisionBlocked,
          icon: 'mdi-receipt-text-edit',
        },
      ],
    },
    {
      title: t('appHeader.navigation.system'),
      icon: 'mdi-tools',
      children: [
        {
          path: '/system/permissions',
          title: t('appHeader.navigation.privileges'),
          available: permissions.value.includes('system:permissions:view') && !divisionBlocked,
          icon: 'mdi-cloud-key-outline',
        },
        {
          path: '/system/read-stores',
          title: "Сховища в пам'яті",
          available: user.value?.role === Roles.ADMIN.value,
          icon: 'mdi-database-outline',
        },
        {
          path: '/system/signal',
          title: t('appHeader.navigation.signal'),
          available: user.value?.role === Roles.ADMIN.value,
          icon: '$signal',
        },
      ]
    },
    {
      title: t('appHeader.navigation.ewStatus'),
      path: '/ew-status',
      available: permissions.value.includes('ew:status') && !divisionBlocked,
      icon: 'mdi-signal',
    },
    {
      title: t('appHeader.navigation.map'),
      path: '/map',
      available: permissions.value.includes('map:view') && !divisionBlocked,
      icon: 'mdi-map-search',
    },
    {
      title: t('appHeader.navigation.analytics'),
      icon: 'mdi-chart-timeline-variant',
      children: [
        {
          path: '/analytics/missions',
          title: t('appHeader.navigation.missionDistribution'),
          available: permissions.value.includes('analytics:general-missions') && !divisionBlocked,
          icon: 'mdi-cast-audio-variant',
        },
        {
          path: '/analytics/daily-results',
          title: t('appHeader.navigation.dailyResults'),
          available: permissions.value.includes('analytics:daily-results') && !divisionBlocked,
          icon: 'mdi-target-account',
        },
        {
          path: '/analytics/channel-heatmap',
          title: t('appHeader.navigation.channelHeatmap'),
          available: permissions.value.includes('analytics:channel-heatmap') && !divisionBlocked,
          icon: 'mdi-heat-wave',
        },
        {
          path: '/analytics/directions',
          title: t('appHeader.navigation.directions'),
          available: permissions.value.includes('analytics:direction-distribution') && !divisionBlocked,
          icon: 'mdi-chart-arc',
        },
      ]
    },
    {
      title: t('appHeader.navigation.help'),
      path: '/help',
      available: permissions.value.includes('help_page:view') && (user.value?.role !== Roles.TEAM.value || pilotAssignedToTeam) && !divisionBlocked,
      icon: 'mdi-help-circle-outline',
    },
    {
      title: t('appHeader.navigation.news'),
      path: '/news',
      available: permissions.value.includes('news_page:view') && (user.value?.role !== Roles.TEAM.value || pilotAssignedToTeam) && !divisionBlocked,
      icon: 'mdi-newspaper',
    },
  ]
})

const {user, signOut, backToAdmin, getSession} = useAuth()
const loadingSignOut = ref(false)
const loadingBackToAdmin = ref<boolean>(false)
const menu = ref(false)
const admins: Ref<any> = ref([])

admins.value.push(['Management', 'mdi-account-multiple-outline'])
admins.value.push(['Settings', 'mdi-cog-outline'])

const {darkTheme, curLocale, openedDrawer} = storeToRefs(useInterfaceStore())

const googleSignOut = async () => {
  loadingSignOut.value = true
  await signOut()
  loadingSignOut.value = false
  menu.value = false
}

const onBackToAdmin = async () => {
  loadingBackToAdmin.value = true
  await backToAdmin()

  await getSession()

  // Clear current user state
  useApp().unload(true)

  // Load new state
  await useApp().load()

  loadingBackToAdmin.value = false
  menu.value = false

  // Використовуємо дефолтну сторінку для ролі користувача
  const defaultPage = getDefaultPageForRole(user.value?.role)
  await navigateTo(defaultPage)
}

const isPushSupported = ref(false)
const checkPushSupported = async () => {
  if (navigator.userAgent.includes('iPhone')) {
    isPushSupported.value = false;
    return
  }

  isPushSupported.value = await isFirebaseSupported();
}
checkPushSupported()

watch(lastMessage, (data: any) => {
  if (data?.refreshLoadedNotificationsData) {
    notificationsStore.load()
  }
})
</script>

<template>
  <v-app-bar color="app-bar">
    <template v-slot:prepend>
      <v-app-bar-nav-icon @click.stop="openedDrawer = !openedDrawer"/>

      <v-avatar size="50" rounded="0">
        <v-img src="/new-logo.png" :cover="false"
        />
      </v-avatar>
    </template>

    <v-app-bar-title>
      <div class="levitation-bar-title">
        Levitation
      </div>
      <div class="levitation-bar-subtitle">
        {{ t('appHeader.toolbar.subtitle') }}
      </div>
    </v-app-bar-title>

    <template v-slot:append>
      <v-btn icon v-if="permissions.includes('notifications:view')" to="/notifications">
        <template v-if="unreadNotificationsCount == 0">
          <v-icon icon="mdi-bell"/>
        </template>
        <template v-else>
          <v-badge color="error" :content="unreadNotificationsCount">
            <v-icon icon="mdi-bell"/>
          </v-badge>
        </template>
      </v-btn>

      <v-btn icon v-if="user?.impersonatedBy">
        <v-icon
            color="error"
            icon="mdi-account-key"
        />
        <v-tooltip
            activator="parent"
            location="start"
            open-on-click
        >
          {{ t('appHeader.userMenu.authorizedAs') }}<br/>
          {{ t('appHeader.userMenu.user') }} <b>{{ user.name }}</b><br/>
          {{ t('appHeader.userMenu.role') }} <b>{{ t('titles.role.' + user.role) }}</b>
        </v-tooltip>
      </v-btn>

      <v-menu v-model="menu" :close-on-content-click="false">
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" icon>
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>

        <v-card min-width="250">
          <v-card-title>
            {{ user?.name }}
          </v-card-title>
          <v-card-text>
            <div class="text-shaded">
              {{ t('titles.role.' + user?.role) }}
            </div>
            <div class="text-shaded" v-if="usersAffiliationTitle">
              {{ usersAffiliationTitle }}
            </div>

          </v-card-text>

          <v-divider></v-divider>

          <v-list>
            <v-list-item>
              <v-switch
                  v-model="darkTheme"
                  :label="t('appHeader.userMenu.darkTheme')"
                  :hide-details="true"
              ></v-switch>
            </v-list-item>
            <v-list-item v-if="isPushSupported">
              <PushSwitcher/>
            </v-list-item>
            <v-list-item v-if="user?.role === Roles.TEAM.value">
              <ShowPreferredChannelsOnlySwitcher />
            </v-list-item>
          </v-list>

          <v-divider></v-divider>

          <v-list>
            <v-list-item>
              <v-list-item-subtitle>
                <v-btn-toggle
                  v-model="curLocale"
                  color="primary"
                  mandatory
                  density="compact"
                  variant="outlined"
                >
                  <v-btn value="uk">{{ t('locales.ua') }}</v-btn>
                  <v-btn value="en">{{ t('locales.en') }}</v-btn>
                </v-btn-toggle>
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>

          <v-divider></v-divider>

          <v-card-actions v-if="user?.impersonatedBy" style="padding-bottom: 0; min-height: fit-content">
            <v-btn
                block
                :text="t('appHeader.userMenu.backToAdmin')"
                variant="tonal"
                color="blue-darken-1"
                append-icon="mdi-security"
                @click="onBackToAdmin"
                :loading="loadingBackToAdmin"
            />
          </v-card-actions>

          <v-card-actions v-if="user?.divisionBlocked !== true">
            <v-btn
                block
                :text="t('appHeader.userMenu.profile')"
                variant="tonal"
                color="primary"
                append-icon="mdi-account-cog-outline"
                @click="navigateTo('/account-settings')"
            />
          </v-card-actions>
          
          <v-card-actions>
            <v-btn
                block
                :text="t('appHeader.userMenu.logout')"
                variant="tonal"
                color="red-darken-1"
                append-icon="mdi-exit-run"
                @click="googleSignOut"
                :loading="loadingSignOut"
            />
          </v-card-actions>
        </v-card>
      </v-menu>

    </template>
  </v-app-bar>

  <v-navigation-drawer width="300" v-model="openedDrawer" mobile-breakpoint="md">
    <v-list>
      <template v-for="route in routesData">
        <template v-if="!route.children">
          <v-list-item
              :prepend-icon="route.icon"
              :title="route.title"
              :to="route.path"
              v-if="route.available"
          >
          </v-list-item>
        </template>
        <template v-else>
          <v-list-group
              :prepend-icon="route.icon"
              :value="route.title"
              v-if="route.children.some(item => item.available)"
          >
            <template v-slot:activator="{ props }">
              <v-list-item
                  v-bind="props"
                  :title="route.title"
              ></v-list-item>
            </template>

            <template v-for="childRoute in route.children">
              <v-list-item
                  :key="childRoute.path"
                  :title="childRoute.title"
                  :to="childRoute.path"
                  v-if="childRoute.available"
                  :prepend-icon="childRoute.icon"
              />
            </template>
          </v-list-group>
        </template>
      </template>
    </v-list>
  </v-navigation-drawer>
</template>

<style>
.v-toolbar-title.v-app-bar-title {
  margin-inline-start: 12px !important;
}

.levitation-bar-title {
  font-size: 23pt;
  line-height: 23pt;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  font-family: "Alumni Sans", sans-serif;
  font-weight: 400;
}

.levitation-bar-subtitle {
  font-size: 15pt;
  line-height: 15pt;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  opacity: 50%;
  font-family: "Alumni Sans", sans-serif;
  font-weight: 400;
}

.v-navigation-drawer__content .v-list-item__spacer {
  width: 16px !important;
}

.v-navigation-drawer__content .v-list-group {
  --prepend-width: 24px;
}
</style>
